import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useApiInfoContextValueHook } from '../contexts/ApiInfoContextProvider';
import { SellertoolSignatureUtils } from '../utils/sellertool-signature-utils';

const API_HOST = process.env.REACT_APP_MAIN_API_HOST;

function buildSignatureHeaders(apiInfo) {
    const timestamp = Date.now().toString();
    const signature = SellertoolSignatureUtils().generateSigniture({
        apiKey: apiInfo?.apiKey,
        secretKey: apiInfo?.secretKey,
        timestamp: timestamp,
    });

    return {
        'x-sellertool-apiKey': apiInfo?.apiKey,
        'x-sellertool-timestamp': timestamp,
        'x-sellertool-signiture': signature,
    };
}

/**
 * 파레트 규격 적재 규칙(workspace 공유)을 조회/저장한다.
 * 규칙은 소수의 설정성 데이터라 전체 교체(bulk-replace) 방식으로 저장한다.
 *
 * @param {object} args
 * @param {boolean} [args.enabled] - 조회 활성화 여부
 */
export function usePalletStandardRulesHook({ enabled = true } = {}) {
    const { apiInfo } = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    const queryKey = ['pallet-standard-rules', apiInfo?.apiKey];

    const query = useQuery({
        queryKey: queryKey,
        queryFn: async () => {
            const res = await axios.get(`${API_HOST}/pallet-standard-rules`, {
                headers: buildSignatureHeaders(apiInfo),
            });
            return res.data?.content ?? [];
        },
        enabled: enabled && !!apiInfo?.apiKey && !!apiInfo?.secretKey,
        staleTime: 1000 * 60 * 5,
    });

    const saveMutation = useMutation({
        mutationFn: async (palletStandardRules) => {
            const res = await axios.post(
                `${API_HOST}/pallet-standard-rules/bulk-replace`,
                { palletStandardRules },
                { headers: buildSignatureHeaders(apiInfo) },
            );
            return res.data?.content ?? [];
        },
        onSuccess: (data) => {
            // 서버가 반환한 최신 목록으로 캐시 갱신
            queryClient.setQueryData(queryKey, data);
        },
    });

    return {
        rules: query.data ?? [],
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        refetch: query.refetch,
        saveRules: saveMutation.mutateAsync,
        isSaving: saveMutation.isPending,
    };
}
